import {
  Component,
  Renderer2,
  ElementRef,
  Input,
  ChangeDetectorRef,
  ViewChild,
} from "@angular/core";
import {
  OrderAuditPromiseService,
  ProfilePromiseActionService,
  Roles,
  SystemConfigPromiseService,
  TableDefinition,
} from "shared/cubiq-services";
import { Subject, debounceTime } from "rxjs";
import * as OrderAuditHelper from "shared/cubiq-services/model/audit/OrderAuditHelper";
import { CoreOrderAudit } from "shared/cubiq-services";
import {
  GoldenLayoutComponent,
  GoldenLayoutServiceProfileConfig,
} from "../golden-layout/golden-layout-component";
import { TranslateService } from "@ngx-translate/core";
import { BfxProtocolType } from "./protocol.type";

const ORDER_PROTOCOL = "ORDER";
const END_OF_LINE = "\r\n";
const CSV_SEP = ",";
const AUDIT_TITLES: Array<string> = ["Date", "Action", "Comments", "User ID", "Order Status"];

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { BfxOrderBlotterAuditService } from "./bfx-order-blotter-audit.service";
import { GoldenLayoutPopout } from "app/golden-layout/golden-layout-popout";
import { OrderAuditTableDefinitionFactory } from "shared/cubiq-services/model/OrderAuditTableDefinitionFactory";
import { BfxBaseBlotter, DEFAULT_ROW_HEIGHT } from "app/bfx-base-blotter/bfx-base-blotter";
import { GoldenLayoutService } from "app/golden-layout/golden-layout-service";
import { BfxBlotterFilterService } from "app/bfx-blotter-filter/bfx-blotter-filter.service";
import { BfxBaseBlotterColumnSelectorService } from "app/bfx-base-blotter/bfx-base-blotter-columnselector.service";
import { BfxMouseModifierKeyService } from "app/bfx-common/services/bfx-mouse-modifier-key.service";
import { BfxSaveProfileNotificationService } from "app/bfx-common/services/bfx-save-profile-notification.service";
import { BfxUserProfileService } from "app/bfx-common/services/bfx-user-profile.service";
import { BlotterType } from "shared/cubiq-model-multi-asset";
import { BfxClipboardService } from "shared/helper-services/bfx-clipboard.service";

@Component({
  selector: "bfx-order-blotter-audit",
  templateUrl: "./html/bfx-order-blotter-audit.html",
  viewProviders: [BfxBaseBlotterColumnSelectorService],
  standalone: false,
})
export class BfxOrderBlotterAuditComponent extends BfxBaseBlotter implements GoldenLayoutComponent {
  @Input() public quote: any;

  private allAuditRecords: CoreOrderAudit[] = [];
  public orderAuditRecords: Array<CoreOrderAudit> = [];

  public protocol: any;

  public parentPopup: GoldenLayoutPopout;

  public showTransfer: boolean = false;

  public showNotification: boolean = false;

  public showRevaluations: boolean = false;

  public showTriggered: boolean = false;

  public isBenchmarkClientOrder: boolean = false;

  public isBenchmarkAlgoOrder: boolean = false;

  public isBenchmarkOrder: boolean = false;

  public canExport: boolean = false;

  public tableDefinition: TableDefinition;

  /** DIV in tempate used for measuring comment dimensions */
  @ViewChild("measureDiv") public measureDiv: ElementRef;

  public frameworkComponents: any = {};

  public refreshRowHeights: Subject<void> = new Subject();

  /** Last text node added to Measure div for measuring */
  private textNode: Text;

  constructor(
    element: ElementRef,
    _tableDefinitionFactory: OrderAuditTableDefinitionFactory,
    layoutService: GoldenLayoutService,
    translationService: TranslateService,
    blotterFilterService: BfxBlotterFilterService,
    columnSelectorService: BfxBaseBlotterColumnSelectorService,
    profileActionService: ProfilePromiseActionService,
    private systemConfigService: SystemConfigPromiseService,
    mouseModifierKeyService: BfxMouseModifierKeyService,
    saveProfileNotificationService: BfxSaveProfileNotificationService,
    protected userProfileService: BfxUserProfileService,
    public orderAuditService: OrderAuditPromiseService,
    public orderBlotterAuditService: BfxOrderBlotterAuditService,
    protected _clipboardService: BfxClipboardService,
    protected _renderer: Renderer2,
    protected cd: ChangeDetectorRef,
  ) {
    super(
      element,
      null,
      null,
      null,
      null,
      null,
      _tableDefinitionFactory,
      layoutService,
      translationService,
      blotterFilterService,
      null,
      columnSelectorService,
      profileActionService,
      systemConfigService,
      mouseModifierKeyService,
      saveProfileNotificationService,
      userProfileService,
    );
    this.blotterId = "order-blotter-audit";
    this.systemConfigService.hasRole(Roles.ROLE_ORDER_EXPORT).then((value) => {
      this.canExport = value;
    });

    this.protocol = BfxProtocolType.ORDER;

    this.showNotification = this.orderBlotterAuditService.showNotification;
    this.showTransfer = this.orderBlotterAuditService.showTransfer;
    this.showRevaluations = this.orderBlotterAuditService.showRevaluations;
    this.showTriggered = this.orderBlotterAuditService.showTriggered;
    // Wait until col resizing has paused for X ms then resize all rows
    this.refreshRowHeights.pipe(debounceTime(200)).subscribe(() => {
      this.gridOptions?.api?.resetRowHeights();
    });
  }

  /**
   * Overriding to remove callback for cntext menu opening
   */
  protected initializeColumnDefinitions(): void {
    const blotterConfig = this.orderBlotterAuditService.columnConfig;
    if (blotterConfig) {
      this.restoreFromConfiguration(blotterConfig);
    }
    super.initializeColumnDefinitions();

    this.gridOptions.onCellContextMenu = null;

    this.gridOptions.api.sizeColumnsToFit();
    this.gridOptions.getRowHeight = (params) => {
      const audit: CoreOrderAudit = params.data;

      const fullHeight = this.measureCommentText(audit.commentsDisplay);
      return fullHeight;
    };
    this.gridOptions.suppressAutoSize = false;

    this.gridOptions.onColumnResized = () => this.refreshRowHeights.next();
    this.gridOptions.onGridSizeChanged = () => {
      try {
        this._agGridResetScroll();
      } catch (e) {
        logger.out.warn(
          `${this.constructor.name}.onGridSizeChanged ag-grid private API use failed.`,
        );
      } finally {
        this.gridOptions?.api?.sizeColumnsToFit();
      }
    };
  }

  public getBlotterType(): BlotterType {
    return BlotterType.OPSINTERVENTION;
  }

  protected applyColumnDefinitions() {
    super.applyColumnDefinitions();

    this.cd.detectChanges();
  }

  protected override initializeInMemory() {
    // Don't call parent
    this.load();
    // this.gridOptions.api.setRowData(this.orderAuditRecords);
  }

  public load() {
    this.orderAuditRecords = [];

    if (this.quote && this.quote.orderId) {
      this.isBenchmarkClientOrder = this.quote.isBenchmarkClientOrder === true;
      this.isBenchmarkAlgoOrder = this.quote.isBenchmarkAlgoOrder === true;
      this.isBenchmarkOrder = this.quote.isBenchmarkOrder === true;

      this.orderAuditService
        .getOrderAudit(this.quote.orderId.toString(), ORDER_PROTOCOL)
        .then((audits: Array<CoreOrderAudit>) => {
          this.allAuditRecords = audits;
          this.applyAuditFilter(audits);
        });
    }
  }

  private applyAuditFilter(audits: CoreOrderAudit[]) {
    const filter = {
      isBenchmark: this.isBenchmarkAlgoOrder || this.isBenchmarkOrder,
      showTrigger: this.showTriggered,
      showRevals: this.showRevaluations,
      showNotifications: this.showNotification,
      showTransfers: this.showTransfer,
    };
    let orderAuditRecordsFiltered = audits.filter((audit) =>
      OrderAuditHelper.isAuditVisible(audit, filter),
    );

    this.orderAuditRecords = orderAuditRecordsFiltered;
    this.gridOptions.api.setRowData(this.orderAuditRecords);
  }

  /**
   * Invoked everytime a checkbox for the filter changes
   */
  reload() {
    this.storeServiceConfig();
    this.load();
    this.gridOptions.api.setRowData(this.orderAuditRecords);
  }
  /**
   * Using the data in allAuditRecords as source of filtering
   */
  refilter() {
    this.applyAuditFilter(this.allAuditRecords);
  }

  /**
   * Store the checkbox filters in the service
   * configuration
   */
  protected storeServiceConfig(storeColumnConfig: boolean = false): void {
    // try {
    this.orderBlotterAuditService.showNotification = this.showNotification;
    this.orderBlotterAuditService.showTransfer = this.showTransfer;
    this.orderBlotterAuditService.showRevaluations = this.showRevaluations;
    this.orderBlotterAuditService.showTriggered = this.showTriggered;
    this.orderBlotterAuditService.columnConfig = this.getComponentConfiguration();
  }

  /**
   * Callback that determines if this popup should be closed or not
   */
  public onClose(): boolean {
    this.storeServiceConfig(true);
    return true;
  }

  glServiceProfileConfig(): GoldenLayoutServiceProfileConfig {
    return this.orderBlotterAuditService;
  }

  setParentPopup(parentPopup: GoldenLayoutPopout) {
    this.parentPopup = parentPopup;
    this.parentPopup.setShowDock(false);
    this.parentPopup.setShowRefresh(true);
  }

  buildStringToCopy(audits: Array<CoreOrderAudit>): string {
    let returnString = "";

    returnString += JSON.stringify(AUDIT_TITLES).replace(/[\[\]]/g, "") + CSV_SEP + END_OF_LINE;
    audits.forEach((element) => {
      let propsToDisplay: Array<string> = [];
      propsToDisplay.push(element.actionTimeStr);
      propsToDisplay.push(element.orderAction);
      propsToDisplay.push(OrderAuditHelper.getCommentsExport(element));
      propsToDisplay.push(element.userStr);
      propsToDisplay.push(element.orderStatus);

      returnString +=
        JSON.stringify(propsToDisplay)
          .replace(/[\[\]]/g, "")
          .replace(/\\/g, "") +
        CSV_SEP +
        END_OF_LINE;
    });

    return returnString;
  }

  public actionExport_copyToClipboard(): void {
    const auditToCopy = this.buildStringToCopy(this.orderAuditRecords);
    try {
      this._clipboardService.setContent(auditToCopy).then(() => {
        if (!this._clipboardService.copyContent()) {
          logger.out.error(
            "ERROR: BfxOrderRecapComponent.actionExport_copyToClipboard(): Copy to clipboard operation failed!",
          );
        }
      });
    } catch (ex) {
      logger.out.error(
        "ERROR: BfxOrderRecapComponent.actionExport_copyToClipboard(): Exception: ",
        ex,
      );
    }
  }

  // Golden layout popout callbacks
  glInit(data: any, container: any, state: any): void {
    this.quote = data;
  }

  /**
   *
   * @param comment using the off-screen div, set the width to our column, then add a text
   * node with our comment and measure the height
   * @returns
   */
  public measureCommentText(comment: string): number {
    const col = this.gridColumnApi.getColumn("commentsDisplay");
    const colWidth = col ? col.getActualWidth() : 100;

    let retHeight = 0;
    if (this.measureDiv) {
      const measureDivElement = this.measureDiv.nativeElement as HTMLDivElement;
      if (this.textNode) {
        measureDivElement.removeChild(this.textNode);
      }
      measureDivElement.style.width = colWidth + "px";
      this.textNode = document.createTextNode(comment);
      measureDivElement.appendChild(this.textNode);
      retHeight = measureDivElement.clientHeight;
    }
    return Math.max(retHeight, DEFAULT_ROW_HEIGHT);
  }
}
